/**
 * Workspace slug helpers. A slug is the human-facing, globally-unique handle
 * that appears in every workspace URL (`/w/<slug>/…`), so it must be
 * URL-safe, lowercase, and stable. Pure + dependency-free so it's trivially
 * testable; uniqueness (a DB concern) lives in OrgsService.ensureUniqueSlug.
 */

/**
 * Reserved slugs a user may not claim, matched EXACTLY (case-insensitive via
 * the normalized slug). Two purposes:
 *  1. route/system/brand words — so a workspace URL never reads as an official
 *     area (`/w/admin`, `/w/billing`) even though `/w/` prevents real routing
 *     collisions;
 *  2. short/ambiguous offensive words (`sex`, `ass`, `homo`, …) that we DON'T
 *     substring-match (to avoid the "Scunthorpe problem" — blocking `essex`,
 *     `assistant`, `homepage`) but still shouldn't be claimable on their own.
 */
export const RESERVED_SLUGS = new Set([
  // structural / routing
  'w', 'api', 'app', 'www', 'cdn', 'static', 'assets', 'public', 'files',
  'ws', 'wss', 'graphql', 'webhook', 'webhooks', 'callback', 'oauth', 'sso',
  // auth + onboarding
  'auth', 'login', 'logout', 'signin', 'signout', 'signup', 'register',
  'setup', 'onboarding', 'invite', 'invites', 'invitation', 'invitations',
  'accept', 'verify', 'reset', 'forgot', 'password',
  // app areas
  'admin', 'administrator', 'root', 'superuser', 'sudo', 'system', 'internal',
  'dashboard', 'settings', 'account', 'accounts', 'profile', 'preferences',
  'billing', 'subscription', 'subscriptions', 'plan', 'plans', 'upgrade',
  'workspace', 'workspaces', 'org', 'orgs', 'organization', 'organizations',
  'team', 'teams', 'member', 'members', 'user', 'users', 'people',
  'project', 'projects', 'prototype', 'prototypes', 'issue', 'issues',
  'run', 'runs', 'loop', 'loops', 'metric', 'metrics', 'doc', 'docs',
  'help', 'support', 'status', 'health', 'feedback', 'search', 'activity',
  'notifications', 'new', 'edit', 'delete', 'create', 'index', 'home',
  // marketing / legal
  'about', 'contact', 'pricing', 'terms', 'privacy', 'legal', 'security',
  'blog', 'news', 'careers', 'jobs', 'press', 'download', 'downloads',
  // infra / well-known
  'mail', 'email', 'ftp', 'ns', 'ns1', 'ns2', 'mx', 'smtp', 'imap', 'pop',
  // sentinels / placeholders
  'null', 'undefined', 'true', 'false', 'nan', 'none', 'test', 'demo',
  'example', 'default', 'aiai', 'aiailoop',
  // short/ambiguous offensive words — exact match only (never substring), so
  // innocent words that merely contain them ("grape", "therapist", "essex")
  // stay allowed.
  'sex', 'ass', 'anal', 'cum', 'fag', 'dyke', 'homo', 'coon', 'spic',
  'chink', 'kike', 'gook', 'wop', 'rape', 'rapist', 'retard', 'xxx', 'porn', 'nsfw',
]);

/**
 * Strong, unambiguous offensive terms rejected ANYWHERE in a slug (dashes
 * stripped, so `acme-fuck-co` is caught). Deliberately excludes short/ambiguous
 * words — those are exact-matched in RESERVED_SLUGS — so we don't false-positive
 * on `grape` ("rape"), `assistant` ("ass"), `document` ("cum"), `raccoon`
 * ("coon"), `homepage` ("homo"), etc. Content-moderation blocklist.
 */
const BLOCKED_SUBSTRINGS = [
  'fuck', 'shit', 'bullshit', 'bitch', 'bastard', 'asshole', 'dickhead',
  'motherfucker', 'cunt', 'pussy', 'whore', 'slut', 'wanker', 'bollocks',
  'nigger', 'nigga', 'faggot', 'molest', 'pedophile', 'paedophile',
  'nazi', 'hitler', 'kkk',
  // Note: 'cunt' does block the town "scunthorpe" — an accepted, rare false
  // positive; the value of blocking the slur anywhere outweighs it.
];

/** True if the slug contains a blocked offensive substring (dashes ignored). */
export function containsBlockedTerm(slug: string): boolean {
  const flat = slug.replace(/-/g, '');
  return BLOCKED_SUBSTRINGS.some((term) => flat.includes(term));
}

/** True if a slug is reserved (exact) or contains a blocked term (substring). */
export function isReservedSlug(slug: string): boolean {
  return RESERVED_SLUGS.has(slug) || containsBlockedTerm(slug);
}

/** Longest slug we keep (keeps URLs and the DB column sane). */
export const MAX_SLUG_LENGTH = 40;

/**
 * Normalize arbitrary text (a workspace name, an email local-part, or a
 * user-typed slug) into a candidate slug: lowercase, accents stripped, every
 * run of non-alphanumerics collapsed to a single dash, trimmed, and truncated.
 * Returns '' when nothing usable remains (caller supplies a fallback).
 */
export function slugify(input: string): string {
  return (input ?? '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '') // drop combining accent marks
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, MAX_SLUG_LENGTH)
    .replace(/-+$/g, ''); // a trailing dash can survive the slice
}

/**
 * Whether a fully-normalized slug is acceptable as a user's chosen workspace
 * URL: 1–40 chars, starts/ends alphanumeric, only `[a-z0-9-]` between, and not
 * reserved. Callers should `slugify()` first, then validate the result.
 */
export function isValidSlug(slug: string): boolean {
  return (
    /^[a-z0-9](?:[a-z0-9-]{0,38}[a-z0-9])?$/.test(slug) &&
    !isReservedSlug(slug)
  );
}
